import { VizPortalSearchResultsDataCatalogProvider } from '@tableau/data-catalog-client-components';
import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { catalogApolloClient } from '../../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../../catalog/CatalogFeatureFlagsAdapter';
import { Formatters, Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { interpolateString } from '../../../common/utilities/StringInterpolation';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import {
  DatabaseDefinition,
  DataRoleDefinition,
  DatasourceDefinition,
  FlowDefinition,
  ProjectDefinition,
  PublishedConnectionDefinition,
  TableDefinition,
  ViewDefinition,
  WorkbookDefinition,
} from '../../../ts/models/ContentDefinitions';
import { MetricDefinition } from '../../../metrics/models/MetricDefinition';
import { DataCatalogApiRequestsService } from '../../../ts/services/DataCatalogApiRequestsService';
import { DataCatalogUIActionsService } from '../../../ts/services/DataCatalogUIActionsService';
import { SearchTermDataContext } from '../../contexts/TelemetryContexts';
import { ServiceLocator } from '../../ServiceLocator';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { FilterMap } from '../../state/ContentExplorerState';
import { SearchSubspaceName, SpaceType } from '../../state/SpaceInfo';
import { ExplorerEmptyState } from '../Components/ExplorerEmpyState';
import { ExplorerTestIds } from '../Explorer/Explorer';
import { Space } from '../Space';
import { addQuery, SpacesSiteUrls } from '../SpacesSiteUrls';
import { explorerSubspace } from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { SearchSpaceStyles } from './SearchSpaceStyles';

export interface SearchSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly searchTerm: string;
  readonly contentFilters: FilterMap;
  readonly contentCounts: ISpaceInfoTypeItemCount<'search'>;
  readonly contentCountsUptoDate: boolean;
  readonly catalogingOnSiteEnabled: boolean;
  readonly areUsersVisible: boolean;
}

export interface SearchSpaceDispatchProps {
  readonly requestSpaceContents: () => void;
  readonly fetchUpdatedContentCounts: () => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface SearchSpaceOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly subspaceName: SearchSubspaceName;
}

export interface SearchSpaceFromAngularProps {
  readonly apiRequestsService: DataCatalogApiRequestsService;
}

export type SearchSpaceProps = SearchSpaceStateProps & SearchSpaceDispatchProps & SearchSpaceOwnProps & SearchSpaceFromAngularProps;

export interface SearchSpaceState {
  readonly hasFetchedFirstResults: boolean;
}

export class SearchSpaceComponent extends React.PureComponent<SearchSpaceProps, SearchSpaceState> {
  constructor(props: SearchSpaceProps) {
    super(props);

    // if content counts are not up to date, we are still fetching data and want to show spinners (not the empty state)
    // so say we have not fetched the results for the first time
    this.state = { hasFetchedFirstResults: !this.props.contentCountsUptoDate };
  }

  private memoizedGetSearchSubspaces = defaultMemoize(getSearchSubspaces);

  public render(): JSX.Element {
    // we don't check the contentCountUpToDate prop becasue we don't want the tabs to flicker in as we
    // search and have no results, but we want them to show the first time we render (TFS922001)
    const emptySearch = this.getTotalResultCount() === 0 && !this.hasExtraFilters() && !this.state.hasFetchedFirstResults;
    if (!emptySearch) {
      return (
        <SearchTermDataContext.Provider value={this.props.searchTerm}>
          <VizPortalSearchResultsDataCatalogProvider // the data catalog (tables, databases) subspaces need this context
            client={catalogApolloClient}
            siteUrls={this.props.urls}
            apiRequests={this.props.apiRequestsService}
            uiActions={DataCatalogUIActionsService.instance()}
            areUsersVisible={this.props.areUsersVisible}
            featureFlags={catalogFeatureFlagsAdapter()}
          >
            {
              this.renderTabbedSearch()
            }
          </VizPortalSearchResultsDataCatalogProvider>
        </SearchTermDataContext.Provider>
      );
    } else {
      return this.renderEmptySearchSpace();
    }
  }

  private renderTabbedSearch(): JSX.Element{
    const subspaces = this.memoizedGetSearchSubspaces(
      this.props.angularContext,
      this.props.contentCounts,
      true,
        // we say contentCounts are always up to date because we don't want to show spinners on tabs as the user types, we just wnat to show the previous values until the new ones come in
      this.props.catalogingOnSiteEnabled,
      this.props.areUsersVisible
    );

    return <TabbedSpace
                subspaces={subspaces}
                activeSubspaceName={this.props.subspaceName}
                urlForSubspace={this.exploreSearchUrl}
                mastheadElement={this.renderMasthead()}
                testIdPrefix={'search'}
                spaceItemName={Messages.searchBrowserTitle()}
            />;
  }

  private renderEmptySearchSpace() {
    const primaryMessage = this.props.searchTerm
      ? Messages.searchSpaceNoResultsPrimary()
      : Messages.searchSpaceNoSearchStringPrimary();
    const secondaryMessage = this.props.searchTerm
      ? Messages.searchSpaceNoResultsSecondary()
      : Messages.searchSpaceNoSearchStringSecondary();
    const renderContent = () => {
      return (
        <span>
          <ExplorerEmptyState
            dataTestId={ExplorerTestIds.ExplorerNoContent}
            iconUrl={imgPath('spaces_empty_search.svg')}
            primaryMessage={primaryMessage}
            secondaryMessage={secondaryMessage}
          />
        </span>
      );
    };
    return <Space renderMasthead={this.renderMasthead} renderContent={renderContent}/>;
  }

  public componentDidMount() {
    if (!this.props.contentCountsUptoDate) {
      this.props.fetchUpdatedContentCounts();
    }
  }

  public componentDidUpdate(prevProps: SearchSpaceProps) {
    if (this.changedSite(prevProps)) {
      this.props.clearContentCounts('search');
      this.props.requestSpaceContents();
    }

    if (prevProps.contentCountsUptoDate && !this.props.contentCountsUptoDate) {
      this.props.fetchUpdatedContentCounts();
    }

    if (this.props.contentCountsUptoDate && this.state.hasFetchedFirstResults) {
      // now that we have content counts, we already fetched results for the first time
      this.setState({ hasFetchedFirstResults: false });
    }
  }

  private changedSite(prevProps: SearchSpaceProps) {
    return !_.isUndefined(this.props.siteName) &&
        prevProps.siteName !== this.props.siteName;
  }

  private static exploreSearchUrlBySubspace: Record<SearchSubspaceName, (urls: SpacesSiteUrls) => string> = {
    datasources: urls => urls.searchDatasourcesUrl(),
    flows: urls => urls.searchFlowsUrl(),
    metrics: urls => urls.searchMetricsUrl(),
    projects: urls => urls.searchProjectsUrl(),
    views: urls => urls.searchViewsUrl(),
    workbooks: urls => urls.searchWorkbooksUrl(),
    tables: urls => urls.searchTablesUrl(),
    databases: urls => urls.searchDatabasesUrl(),
    dataRoles: urls => urls.searchDataRolesUrl(),
    users: urls => urls.searchUsersUrl(),
    publishedConnections: urls => urls.searchPublishedConnectionsUrl(),
  };

  private exploreSearchUrl = (subspaceName?: SearchSubspaceName) => {
    let url = this.props.urls.searchViewsUrl();
    if (subspaceName) {
      const urlFn = SearchSpaceComponent.exploreSearchUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls) || url;
    }
    return addQuery(url);
  }

  private renderMasthead = () => {
    return(
      <div>
        <div style={{display: 'flex', flexDirection: 'column'}}>
          <span className={SearchSpaceStyles.Results}>
            {Messages.searchSpaceResults()}
          </span>
          <span data-tb-test-id="search-detail-results" className={SearchSpaceStyles.ResultsDetails}>
            {this.resultsDetails()}
          </span>
        </div>
      </div>
    );
  }

  private replacements = (totalResults: number = 0) => {
    return {
      ITEM_COUNT:  <span key={1} className={SearchSpaceStyles.ResultsDetailsBold}>{Formatters.formatCount(totalResults)}</span>,
      SEARCH_TERM: <span key={2} className={SearchSpaceStyles.ResultsDetailsSearchTerm}>{this.props.searchTerm}</span>,
      SPINNER: <span key={1} aria-busy="true" style={{paddingRight: CommonStyles.Sizes.DefaultSpace, paddingLeft: CommonStyles.Sizes.DefaultSpace}}>
                 <ActivitySpinnerWidget testId={'search-space-waiting'} spinnerSize={ActivitySpinnerSize.Small} />
               </span>
    };
  }

  private resultsDetails = () => {
    if (this.props.searchTerm.length === 0 || _.isUndefined(this.props.contentCounts)) {
      return '';
    } else if (!this.props.contentCountsUptoDate) {
      return interpolateString(Messages.searchSpaceResultsDetailsSpinner(), this.replacements());
    } else {
      const total = this.getTotalResultCount();
      if (total === 0) {
        return interpolateString(Messages.searchSpaceResultsDetailsNone(), this.replacements());
      } else if (total === 1) {
        return interpolateString(Messages.searchSpaceResultsDetailsOne(), this.replacements(total));
      } else {
        return interpolateString(Messages.searchSpaceResultsDetailsMultiple(), this.replacements(total));
      }
    }
  }

  private getTotalResultCount(): number {
    return _.values(this.props.contentCounts)
      .reduce((prevValue: number, currentValue: number) => this.getValue(prevValue) + this.getValue(currentValue), 0);
  }

  private hasExtraFilters(): boolean {
    // With no additional filters applied, there should only be 1 filter in the app store (the 'search' filter)
    return Object.keys(this.props.contentFilters).length > 1;
  }

  private getValue(val: number): number {
    return _.isNaN(val) ? 0 : val;
  }
}

export function getNoResultsMessage(searchTerm: string) {
  return interpolateString(Messages.searchSpaceResultsDetailsNone(), { SEARCH_TERM: searchTerm }).join('');
}

function getSearchSubspaces(
  angularContext: AngularContext,
  contentCounts: ISpaceInfoTypeItemCount<'search'>,
  contentCountsUpToDate: boolean,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean
): ReadonlyArray<Subspace> {
  return _.compact([
    explorerSubspace({
      name: 'views',
      tabLabel: Messages.views(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['views'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: ViewDefinition.pluralName() }),
      },
    }),
    FeatureFlags.metricsSpaceEnabled && explorerSubspace({
      name: 'metrics',
      tabLabel: Messages.metrics(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['metrics'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: MetricDefinition.pluralName() }),
      },
    }),
    FeatureFlags.publishedConnectionContentManagementEnabled && explorerSubspace({
      name: 'publishedConnections',
      tabLabel: Messages.publishedConnections(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['publishedConnections'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: PublishedConnectionDefinition.pluralName() }),
      },
    }),
    explorerSubspace({
      name: 'workbooks',
      tabLabel: Messages.workbooks(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['workbooks'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: WorkbookDefinition.pluralName() }),
      },
    }),
    explorerSubspace({
      name: 'datasources',
      tabLabel: Messages.pageHeaderDatasources(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['datasources'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: DatasourceDefinition.pluralName() }),
      },
    }),
    explorerSubspace({
      name: 'flows',
      tabLabel: Messages.flows(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['flows'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: FlowDefinition.pluralName() }),
      },
    }),
    explorerSubspace({
      name: 'projects',
      tabLabel: Messages.projects(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['projects'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: ProjectDefinition.pluralName() }),
      },
    }),
    catalogingOnSiteEnabled && explorerSubspace({
      name: 'tables',
      tabLabel: Messages.tables(),
      supportedDisplayModes: ['list'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['tables'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: TableDefinition.pluralName() }),
      },
    }),
    catalogingOnSiteEnabled && explorerSubspace({
      name: 'databases',
      tabLabel: Messages.databasesAndFiles(),
      supportedDisplayModes: ['list'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['databases'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: DatabaseDefinition.pluralName() }),
      },
    }),
    explorerSubspace({
      name: 'dataRoles',
      tabLabel: Messages.dataRoles(),
      supportedDisplayModes: ['list', 'thumbnail'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: false,
      supportFilterPanel: true,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['dataRoles'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: DataRoleDefinition.pluralName() }),
      },
    }),
    areUsersVisible && explorerSubspace({
      name: 'users',
      tabLabel: Messages.users(),
      supportedDisplayModes: ['list'],
      angularContext,
      toolbarVisibility: 'enabled',
      hideSelection: true,
      supportFilterPanel: false,
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      contentCount: contentCounts && contentCounts['users'],
      emptyStateProps: {
        primaryMessage: Messages.searchSpaceNoResultsForContentType({ CONTENT_TYPE: Messages.users() }),
      },
    }),
  ]);

}

export namespace SearchSpaceAngularProvider {
  interface AngularServices {
    readonly apiRequestsService: DataCatalogApiRequestsService;
  }

  function getAngularServicesWithServiceLocator() {
    const apiRequestsService = ServiceLocator.get<DataCatalogApiRequestsService>('DataCatalogApiRequestsService');
    return {
      apiRequestsService
    };
  }

  function selectPropsFromAngularServices(angularServices: AngularServices) {
    return {
      apiRequestsService: angularServices.apiRequestsService
    };
  }

  export const HOC = angularProviderHOC<SearchSpaceOwnProps & SearchSpaceDispatchProps & SearchSpaceStateProps, SearchSpaceFromAngularProps, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

export const SearchSpace: React.ComponentType<SearchSpaceOwnProps & SearchSpaceDispatchProps & SearchSpaceStateProps> =
  SearchSpaceAngularProvider.HOC(SearchSpaceComponent);
